import { useState, useEffect, useCallback } from 'react';
import { apiClient } from 'api';
import { useComparisonCacheStore } from 'state/zustand/comparisonCache';

export interface ApiComparisonCategory {
  category: string;
  candidates: Array<{ candidate_id: number; score: number }>;
}

export interface ApiCandidateProfile {
  name: string;
  email: string;
  phone: string;
  portfolio: string;
  cv: string;
  date: string;
  avg_score: number;
}

export interface ApiCandidate {
  id: number;
  profile: ApiCandidateProfile;
  scores: Record<string, number>;
}

export interface CompareCandidatesRequestData {
  candidate_response_ids: number[];
}

export interface CompareCandidatesResponseData {
  message: string;
  data: {
    candidates: ApiCandidate[];
    comparisons: ApiComparisonCategory[];
    ai_opinion?: {
      candidate_analyses?: any[];
      comparison?: string;
      recommendation?: string;
    };
  };
}

export const useCompareCandidates = (candidateIds: number[]) => {
  const { getCache, setCache } = useComparisonCacheStore();
  const [data, setData] = useState<CompareCandidatesResponseData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const idsKey = [...candidateIds].sort((a, b) => a - b).join(',');

  const fetchComparison = useCallback(async () => {
    if (candidateIds.length < 2) return;

    // Check Zustand persisted cache first
    const cached = getCache(candidateIds);
    if (cached) {
      setData(cached);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await apiClient.post<CompareCandidatesResponseData>(
        '/reports/compare/',
        { candidate_response_ids: candidateIds }
      );
      setData(response.data);
      setCache(candidateIds, response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey]);

  useEffect(() => {
    fetchComparison();
  }, [fetchComparison]);

  return {
    data,
    isLoading,
    error,
  };
};
